import { Box, IconButton } from "@mui/material";
import { Delete, DragIndicator } from "@mui/icons-material";
import { useSortable, defaultAnimateLayoutChanges } from "@dnd-kit/sortable";
import type { AnimateLayoutChanges } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";

// Disable animations when items are reordered to prevent double-shuffle effect
const animateLayoutChanges: AnimateLayoutChanges = (args) => {
  const { isSorting, wasDragging } = args;
  if (isSorting || wasDragging) {
    return false;
  }
  return defaultAnimateLayoutChanges(args);
};

interface SortableEntryWrapperProps {
  id: string;
  onRemove: () => void;
  canRemove: boolean;
  children: React.ReactNode;
}

export default function SortableEntryWrapper({
  id,
  onRemove,
  canRemove,
  children,
}: SortableEntryWrapperProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, animateLayoutChanges });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1,
      }}
    >
      <IconButton
        size="small"
        sx={{ cursor: "grab", touchAction: "none" }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator />
      </IconButton>
      <Box sx={{ flex: 1 }}>{children}</Box>
      <IconButton
        size="small"
        onClick={onRemove}
        disabled={!canRemove}
        color="error"
        sx={{ visibility: canRemove ? "visible" : "hidden" }}
      >
        <Delete />
      </IconButton>
    </Box>
  );
}
